import { applyMove, boardOf, cellOf, legalMoves, opponent } from '../engine.js';
import type { GameState, Move } from '../types.js';
import { SQUARE_WEIGHT, canWinMiniBoard, metaSlots, winningCells } from './position.js';
import type { Bot, BotContext } from './types.js';

/**
 * Level 2: one-ply scoring with the tactics that matter most in this game.
 *
 * No search — every move is judged by the position it produces. The scoring
 * weights below are ordered so that a decisive tactic always outranks a
 * positional preference.
 */

const SCORE = {
  /** Wins the whole game. Nothing outranks this. */
  gameWin: 100_000,
  /** Hands the opponent the game on their reply. */
  gameLoss: -100_000,
  /** Claims a mini-board. */
  miniWin: 250,
  /** Denies the opponent a mini-board they were one move from taking. */
  block: 200,
  /** Sends the opponent to a board where they can immediately claim a win. */
  feedsMiniWin: -150,
  /** Sends the opponent anywhere they like, which is close to a free tempo. */
  grantsFreeChoice: -80,
} as const;

/** Meta-lines through each mini-board, precomputed — this is on the hot path. */
const LINES_THROUGH: readonly (readonly (readonly number[])[])[] = Array.from(
  { length: 9 },
  (_, board) =>
    [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8],
      [0, 4, 8],
      [2, 4, 6],
    ].filter((line) => line.includes(board)),
);

/** How much a mini-board is worth to `player`, given the meta-lines it sits on. */
function metaValue(state: GameState, board: number, player: 'X' | 'O'): number {
  const slots = metaSlots(state.miniResults);
  const foe = opponent(player);

  // Only lines the opponent has not already broken can still be won.
  const lines = LINES_THROUGH[board].filter((line) => !line.some((i) => slots[i] === foe));

  let value = SQUARE_WEIGHT[board] * 10;
  for (const line of lines) {
    const mine = line.filter((i) => slots[i] === player).length;
    value += mine === 2 ? 400 : mine === 1 ? 80 : 20;
  }
  return value;
}

function scoreMove(state: GameState, move: Move): number {
  const me = state.turn;
  const foe = opponent(me);
  const board = boardOf(move);
  const cell = cellOf(move);

  const applied = applyMove(state, move);
  if (!applied.ok) return Number.NEGATIVE_INFINITY;
  const next = applied.state;

  if (next.result === me) return SCORE.gameWin;

  let score = 0;

  // Claiming a mini-board, valued by the meta-lines it advances.
  if (next.miniResults[board] === me && state.miniResults[board] === null) {
    score += SCORE.miniWin + metaValue(state, board, me);
  }

  // Denying a mini-board the opponent could have taken at this exact cell.
  if (winningCells(state.boards[board], foe).includes(cell)) {
    score += SCORE.block + metaValue(state, board, foe) / 2;
  }

  // Where this move sends the opponent.
  if (next.activeBoard === null) {
    score += SCORE.grantsFreeChoice;
    // Worse still if some open board is sitting there won for the taking.
    for (let i = 0; i < 9; i++) {
      if (canWinMiniBoard(next, i, foe)) {
        score += SCORE.feedsMiniWin;
        break;
      }
    }
  } else if (canWinMiniBoard(next, next.activeBoard, foe)) {
    score += SCORE.feedsMiniWin + -metaValue(next, next.activeBoard, foe) / 2;
  }

  // Positional tie-breaks: good cell, good board.
  score += SQUARE_WEIGHT[cell] * 2 + SQUARE_WEIGHT[board];

  return score;
}

export const heuristicBot: Bot = {
  id: 'heuristic',
  name: 'Casual',
  level: 2,
  description: 'Takes and blocks mini-boards, and avoids handing over free moves.',

  chooseMove(state: GameState, ctx: BotContext): Move {
    const moves = legalMoves(state);
    if (moves.length === 0) throw new Error('no legal moves');

    let best: Move[] = [];
    let bestScore = Number.NEGATIVE_INFINITY;

    for (const move of moves) {
      const score = scoreMove(state, move);
      if (score > bestScore) {
        bestScore = score;
        best = [move];
      } else if (score === bestScore) {
        best.push(move);
      }
    }

    // Break ties randomly so the bot is not perfectly predictable.
    return best[Math.floor(ctx.random() * best.length)];
  },
};

export { scoreMove as scoreMoveForTesting };
